/**
 * Утилита для генерации детерминированных эмодзи-аватарок игроков
 * Использует seeded random для синхронизации между клиентами
 */

const AVATAR_EMOJIS = [
    '🐶', '🐱', '🐭', '🐹', '🐰', '🦊', '🐻', '🐼',
    '🐨', '🐯', '🦁', '🐮', '🐷', '🐸', '🐵', '🐔',
    '🦄', '🦉', '🦆', '🦅', '🐺', '🐗', '🐴', '🦓',
    '🦒', '🦘', '🦝', '🦙', '🦛', '🦏', '🐘', '🦍'
];

/**
 * Генератор детерминированных эмодзи для игроков
 * Использует hash от player ID для выбора уникального эмодзи
 */
class EmojiGenerator {
    constructor() {
        this.gameEmojiMap = new Map(); // gameId -> Map(playerId -> emoji)
    }

    /**
     * Получить эмодзи для игрока в конкретной игре
     * @param {string} gameId - ID игры
     * @param {string[]} allPlayerIds - Все ID игроков в игре (для уникальности)
     * @param {string} playerId - ID конкретного игрока
     */
    getEmojiForPlayer(gameId, allPlayerIds, playerId) {
        // Check cache first
        if (this.gameEmojiMap.has(gameId)) {
            const playerMap = this.gameEmojiMap.get(gameId);
            if (playerMap.has(playerId)) {
                return playerMap.get(playerId);
            }
        }

        // Generate emojis for all players in this game
        if (!this.gameEmojiMap.has(gameId)) {
            this.gameEmojiMap.set(gameId, new Map());
        }

        const playerMap = this.gameEmojiMap.get(gameId);
        const usedEmojis = new Set(playerMap.values());

        // Sort player IDs for deterministic order
        const sortedPlayers = [...allPlayerIds].sort();

        // Assign emojis to players who don't have one yet
        for (const pid of sortedPlayers) {
            if (!playerMap.has(pid)) {
                // Use hash to pick emoji
                const hash = this.hashString(pid);
                let emoji;
                let attempts = 0;

                // Find unused emoji using hash
                do {
                    const index = (hash + attempts) % AVATAR_EMOJIS.length;
                    emoji = AVATAR_EMOJIS[index];
                    attempts++;
                } while (usedEmojis.has(emoji) && attempts < AVATAR_EMOJIS.length);

                playerMap.set(pid, emoji);
                usedEmojis.add(emoji);
            }
        }

        return playerMap.get(playerId) || AVATAR_EMOJIS[0];
    }

    /**
     * Простая хэш-функция для строки
     */
    hashString(str) {
        let hash = 0;
        for (let i = 0; i < str.length; i++) {
            const char = str.charCodeAt(i);
            hash = ((hash << 5) - hash) + char;
            hash = hash & hash; // Convert to 32bit integer
        }
        return Math.abs(hash);
    }

    /**
     * Очистить кэш для игры
     */
    resetGame(gameId) {
        this.gameEmojiMap.delete(gameId);
    }

    /**
     * Полный сброс
     */
    reset() {
        this.gameEmojiMap.clear();
    }
}

// Глобальный экземпляр
const emojiGenerator = new EmojiGenerator();

/**
 * Получить эмодзи для игрока
 * @param {string} playerId - ID игрока
 * @param {string} gameId - ID игры (опционально)
 * @param {string[]} allPlayerIds - Все игроки в игре (опционально)
 */
export function getPlayerEmoji(playerId, gameId = 'default', allPlayerIds = [playerId]) {
    return emojiGenerator.getEmojiForPlayer(gameId, allPlayerIds, playerId);
}

/**
 * Сбросить эмодзи для игры
 */
export function resetGameEmojis(gameId) {
    emojiGenerator.resetGame(gameId);
}

/**
 * Сбросить все эмодзи
 */
export function resetPlayerEmojis() {
    emojiGenerator.reset();
}
